import { Grid, Table, DiffTable } from '@oliasoft-open-source/react-ui-library';
import { convertAndGetValue } from '@oliasoft-open-source/units';
import { getData } from 'common/table-viewer/get-table-data';
import { createUnitCell } from 'common/table-viewer/utils';
import { IObject } from 'common/interfaces/hierarchy-entities';
import { TFunction } from 'i18next';
import { round } from 'es-toolkit/compat';
import { useState } from 'react';
import { formatTableTooltip } from './format-table-tooltip';
import { EntityType } from 'common/enums/entity-enums';
import { surveyRunLinkedEntityTypes } from './table-viewer';
import translations from 'client/internationalization/translation-map.json';
import { ModalType } from 'common/enums/information-enums';

interface IInfoTableProps {
  dataObject: IObject;
  t: TFunction;
  unitTemplate: { [key: string]: string };
  handleLinkedObjectClick: (value: string | undefined, modalType: ModalType) => void;
  dataToCompare?: { [k: string]: any };
}

export const InfoTable = ({ dataObject, t, unitTemplate, handleLinkedObjectClick , dataToCompare}: IInfoTableProps) => {
  const tableData = getData(dataObject, unitTemplate);
  const {
    headers, rows: dataRows, units: dataUnits, tooltips, quantities,
  } = tableData;
  const [units, setUnits] = useState<string[]>(dataUnits);

  const isSurveyRunLinkedEntity = surveyRunLinkedEntityTypes.includes(dataObject.type as EntityType);

  const changeUnit = (unit: string, index: number) => {
    const newUnits = [...units];
    newUnits[index] = unit;
    setUnits(newUnits);
  };

  const buildRows = (sourceRow: (string | number)[]) => headers.map((header, index) => {
    const toUnit = units[index];
    const fromUnit = dataUnits[index];
    const value = sourceRow?.[index];
    const tooltip = tooltips?.[index] || null;
    const quantity = quantities?.[index];

    const cells = [];
    cells.push({
      value: header,
    });

    if (toUnit && toUnit !== '') {
      cells.push({
        value: round(convertAndGetValue(value, toUnit, fromUnit), 3),
      });
      cells.push(dataToCompare
        ? { value: toUnit }
        : createUnitCell(toUnit, quantity, (unit: string) => changeUnit(unit, index)));
    } else {
      cells.push({
        value,
        colSpan: 2,
      });
    }

    return {
      cells,
      actions: [{
        icon: 'help',
        tooltip: tooltip
        && (
        <Grid>
          {formatTableTooltip(tooltip, t)}
        </Grid>
        ),
      },
      isSurveyRunLinkedEntity && {
        icon: 'link',
        tooltip: <Grid>
          {t(translations.objects_surveyItemNavigation)}
        </Grid>,
        onClick: () => handleLinkedObjectClick(undefined, ModalType.MultipleLinkedItems)
      }
    ],
    };
  });

  const table = {
    rows: buildRows(dataRows?.[0]),
    bordered: false,
    maxHeight: '80vh',
    estimateRowHeight: () => 40,
  };

  if (dataToCompare) {
    const compareDataRow = getData({ ...dataObject, data: dataToCompare }, unitTemplate).rows?.[0];
    return (
      <DiffTable
        table={table}
        compareRows={buildRows(compareDataRow)}
      />
    );
  }

  return <Table table={table} />;
};
